import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { 
  Eye, 
  Check, 
  X, 
  Search, 
  Filter, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Calendar,
  Building2,
  Phone,
  User,
  Star,
  Layers,
  MoreHorizontal,
  ShieldAlert,
  UserCheck,
  PlayCircle,
  Ban,
  FileText,
  Paperclip,
  CreditCard,
  Download,
  AlertCircle,
} from 'lucide-react';
import dayjs from 'dayjs';
import apiClient from '../../utils/apiClient';
import { message, Modal, Dropdown, Tag, Image } from 'antd';
import VietnameseDatePicker from '../../components/documents/VietnameseDatePicker';
import CustomSelect from '../../components/base/CustomSelect';

// Cấu hình chống spam thông báo toàn cục: Luôn chỉ hiện tối đa 1 thông báo
message.config({
  maxCount: 1,
  duration: 3,
});

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function LeaderMeetingFeedbackPage() {
  const currentUser = useSelector((state) => state.auth?.user);
  const currentProfile = useSelector((state) => state.userProfile?.profile);
  const currentUserId = currentUser?.userId || currentUser?.id || currentProfile?.id;
  const currentUsername = currentUser?.username || currentProfile?.ten_dang_nhap || currentProfile?.username;
  const currentFullName = currentProfile?.ho_va_ten || currentProfile?.fullName || currentUser?.ho_va_ten;

  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [attachmentBlobs, setAttachmentBlobs] = useState({});
  const [downloadingAttId, setDownloadingAttId] = useState(null);
  const [cccdLoading, setCccdLoading] = useState(false);

  // Filter states
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Reject / Cancel Modal state
  const [reasonModal, setReasonModal] = useState({
    open: false,
    type: 'REJECT', // 'REJECT' | 'CANCEL'
    target: null,
    reason: '',
    loading: false
  });

  // Note Modal state (for Process / Complete)
  const [noteModal, setNoteModal] = useState({
    open: false,
    type: 'PROCESS', // 'PROCESS' | 'COMPLETE'
    target: null,
    note: '',
    loading: false
  });

  // Tickets state from API
  const [dbTickets, setDbTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    inProgress: 0,
    completed: 0,
    rejected: 0,
    canceled: 0,
    overdue: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const params = {};
      if (currentUserId) {
        params.leaderId = currentUserId;
      }
      if (dateFilter) {
        params.fromDate = dateFilter;
        params.toDate = dateFilter;
      }
      const res = await apiClient.get('/api/leader-meeting-registrations/statistics', { params });
      if (res.data?.data) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.warn("Failed to fetch leader meeting statistics", err);
    } finally {
      setStatsLoading(false);
    }
  }, [currentUserId, dateFilter]);


  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    pageSize: 10,
    totalPages: 1,
    totalItems: 0
  });

  // Helper kiểm tra đơn có thuộc lãnh đạo đang đăng nhập hay không
  const isMyRegistration = useCallback((item) => {
    if (!currentUserId && !currentUsername && !currentFullName) return true;
    const lId = item.leader?.id || item.leaderId;
    const lName = item.leader?.fullName || item.leaderName || item.schedule?.officerName || item.approver?.name;
    
    if (currentUserId && lId && String(lId) === String(currentUserId)) {
      return true;
    }
    if (currentUsername && lName && lName.toLowerCase() === currentUsername.toLowerCase()) {
      return true;
    }
    if (currentFullName && lName && lName.toLowerCase() === currentFullName.toLowerCase()) {
      return true;
    }
    return !lId && !lName;
  }, [currentUserId, currentUsername, currentFullName]);

  const fetchTickets = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        size: 10
      };
      if (currentUserId) {
        params.leaderId = currentUserId;
      }
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (dateFilter) {
        params.fromDate = dateFilter;
        params.toDate = dateFilter;
      }

      const res = await apiClient.get('/api/leader-meeting-registrations', { params });
      const payload = res.data;
      const dataList = payload?.data || [];
      const arrayList = Array.isArray(dataList) ? dataList : [];
      const myTickets = (currentUserId || currentUsername || currentFullName)
        ? arrayList.filter(isMyRegistration)
        : arrayList;

      setDbTickets(myTickets);
      if (payload?.pagination) {
        setPagination({
          pageSize: payload.pagination.pageSize || 10,
          totalPages: payload.pagination.totalPages || 1,
          totalItems: (currentUserId || currentUsername || currentFullName) ? myTickets.length : (payload.pagination.totalItems || myTickets.length)
        });
      }
    } catch (error) {
      console.error("Failed to fetch leader meeting tickets", error);
      // Fallback local if 403 or error
      setDbTickets([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, dateFilter, currentUserId, currentUsername, currentFullName, isMyRegistration]);

  useEffect(() => {
    setCurrentPage(1);
    fetchTickets(1);
  }, [fetchTickets]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);


  const handleCloseDetail = () => {
    Object.values(attachmentBlobs).forEach(url => {
      try { URL.revokeObjectURL(url); } catch (e) {}
    });
    setAttachmentBlobs({});
    setSelectedMeeting(null);
  };

  const handleDownloadAttachment = async (registrationId, att) => {
    try {
      setDownloadingAttId(att.id);
      const endpoint = att.contentEndpoint || `/api/leader-meeting-registrations/${registrationId}/attachments/${att.id}`;
      const res = await apiClient.get(
        endpoint,
        { 
          params: { download: true },
          responseType: 'blob' 
        }
      );
      const blob = new Blob([res.data], { type: att.mimeType || 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = att.originalName || 'tai_lieu_dinh_kem';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      message.success(`Đã tải xuống ${att.originalName || 'tệp đính kèm'}`);
    } catch (err) {
      console.error('Lỗi khi tải tệp:', err);
      let errorMsg = 'Không thể tải xuống tệp đính kèm';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          if (json?.message) errorMsg = json.message;
        } catch (_) {}
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      message.error(errorMsg);
    } finally {
      setDownloadingAttId(null);
    }
  };

  const handleOpenDetail = async (meeting) => {
    setSelectedMeeting(meeting);
    const targetId = meeting.rawId || meeting.id || meeting.receptionId;
    if (!targetId) return;

    try {
      setDetailLoading(true);
      const res = await apiClient.get(`/api/leader-meeting-registrations/${targetId}`);
      const d = res.data?.data || res.data;
      if (d) {
        const app = d.applicant || d.citizen || d.citizenInfo || {};
        const p = app.phoneNumber || app.phone || d.phone || d.phoneNumber || d.citizenPhone || meeting.phone;
        const c = app.citizenId || app.identityCardNumber || app.cccd || d.cccd || d.citizenId || d.identityCardNumber || meeting.cccd;
        const a = app.address || d.address || d.citizenAddress || meeting.address;
        const n = app.fullName || app.name || d.citizenName || d.applicantName || d.fullName || meeting.citizenName;
        const t = d.reason || d.topic || d.workingContent || d.content || d.title || meeting.topic;
        const desc = d.workingContent || d.reason || d.description || d.content || meeting.description || t;
        const leader = d.leader?.fullName || d.leaderName || d.schedule?.officerName || d.approver?.name || meeting.leaderName;

        setSelectedMeeting(prev => ({
          ...prev,
          ...d,
          ticketNo: d.registrationCode || d.receptionCode || meeting.ticketNo,
          receptionId: d.id || meeting.receptionId,
          rawId: d.id || meeting.rawId,
          topic: t,
          description: desc,
          date: d.receptionDate ? new Date(d.receptionDate).toLocaleDateString('vi-VN') : (meeting.date || meeting.receptionDate),
          slot: d.timeSlot || meeting.slot,
          fullName: n,
          citizenName: n,
          phone: p && p !== '---' ? p : meeting.phone,
          cccd: c && c !== '---' ? c : meeting.cccd,
          address: a && a !== '---' ? a : meeting.address,
          leaderName: leader || 'Lãnh đạo UBND',
          leaderPosition: d.approver?.title || d.leader?.position || meeting.leaderPosition || 'Lãnh đạo phụ trách',
          office: d.location || d.schedule?.location || meeting.office || 'Phòng tiếp công dân',
          approvalStatus: d.approvalStatus || d.status || meeting.status,
          status: d.approvalStatus || d.status || meeting.status,
          attachments: d.attachments || []
        }));

        // Tải trước ảnh CCCD / ảnh đính kèm
        if (Array.isArray(d.attachments) && d.attachments.length > 0) {
          setCccdLoading(true);
          const blobsMap = {};
          await Promise.all(
            d.attachments.map(async (att) => {
              if (
                att.type === 'CCCD_FRONT' ||
                att.type === 'CCCD_BACK' ||
                att.type === 'IDENTITY_FRONT' ||
                att.type === 'IDENTITY_BACK' ||
                att.mimeType?.startsWith('image/')
              ) {
                try {
                  const endpoint = att.contentEndpoint || `/api/leader-meeting-registrations/${targetId}/attachments/${att.id}`;
                  const blobRes = await apiClient.get(
                    endpoint,
                    { responseType: 'blob' }
                  );
                  const objUrl = URL.createObjectURL(blobRes.data);
                  blobsMap[att.id] = objUrl;
                } catch (e) {
                  console.warn(`Could not load attachment image ${att.id}`, e);
                }
              }
            })
          );
          setAttachmentBlobs(blobsMap);
          setCccdLoading(false);
        } else {
          setAttachmentBlobs({});
        }
      }
    } catch (err) {
      console.warn("Could not fetch remote leader meeting detail, using row data", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const frontCccd = useMemo(() => {
    if (!selectedMeeting?.attachments) return null;
    return selectedMeeting.attachments.find(
      a => a.type === 'CCCD_FRONT' || a.type === 'IDENTITY_FRONT' || a.originalName?.toLowerCase().includes('mat_truoc') || a.originalName?.toLowerCase().includes('front')
    );
  }, [selectedMeeting]);

  const backCccd = useMemo(() => {
    if (!selectedMeeting?.attachments) return null;
    return selectedMeeting.attachments.find(
      a => a.type === 'CCCD_BACK' || a.type === 'IDENTITY_BACK' || a.originalName?.toLowerCase().includes('mat_sau') || a.originalName?.toLowerCase().includes('back')
    );
  }, [selectedMeeting]);

  const supportingDocs = useMemo(() => {
    if (!selectedMeeting?.attachments) return [];
    return selectedMeeting.attachments.filter(
      a => a.id !== frontCccd?.id && a.id !== backCccd?.id
    );
  }, [selectedMeeting, frontCccd, backCccd]);

  // 1. Approve Meeting
  const handleApprove = async (meeting) => {
    if (actionLoading) return;
    const targetId = meeting.rawId || meeting.id || meeting.receptionId;
    try {
      setActionLoading(true);
      await apiClient.patch(`/api/leader-meeting-registrations/${targetId}/approve`, {});
      message.success({ content: 'Đã phê duyệt lịch gặp lãnh đạo thành công!', key: 'action_success' });
      fetchTickets(currentPage);
      fetchStats();
      if (selectedMeeting?.rawId === targetId) {
        setSelectedMeeting(prev => ({ ...prev, status: 'APPROVED', approvalStatus: 'APPROVED' }));
      }
    } catch (error) {
      console.error("Failed to approve leader meeting", error);
      message.error({ content: error.response?.data?.message || 'Lỗi khi phê duyệt lịch gặp', key: 'action_error' });
    } finally {
      setActionLoading(false);
    }
  };

  // 2. Open Reject / Cancel Modal
  const handleOpenReasonModal = (meeting, type = 'REJECT') => {
    setReasonModal({
      open: true,
      type,
      target: meeting,
      reason: '',
      loading: false
    });
  };

  // 3. Confirm Reject or Cancel
  const handleConfirmReason = async () => {
    if (!reasonModal.target || reasonModal.loading) return;
    const trimmed = reasonModal.reason.trim();
    if (!trimmed || trimmed.length < 5) {
      message.warning({ content: 'Vui lòng nhập lý do tối thiểu 5 ký tự!', key: 'form_validation' });
      return;
    }
    if (trimmed.length > 2000) {
      message.warning({ content: 'Lý do không được vượt quá 2000 ký tự!', key: 'form_validation' });
      return;
    }
    const targetId = reasonModal.target.rawId || reasonModal.target.id || reasonModal.target.receptionId;
    const endpoint = reasonModal.type === 'CANCEL' 
      ? `/api/leader-meeting-registrations/${targetId}/cancel`
      : `/api/leader-meeting-registrations/${targetId}/reject`;

    try {
      setReasonModal(prev => ({ ...prev, loading: true }));
      await apiClient.patch(endpoint, {
        reason: trimmed
      });
      message.success({ content: reasonModal.type === 'CANCEL' ? 'Đã hủy lịch hẹn gặp lãnh đạo' : 'Đã từ chối đơn đăng ký gặp lãnh đạo', key: 'action_success' });
      setReasonModal({ open: false, type: 'REJECT', target: null, reason: '', loading: false });
      fetchTickets(currentPage);
      fetchStats();
      if (selectedMeeting?.rawId === targetId) {
        setSelectedMeeting(null);
      }
    } catch (error) {
      console.error("Action error", error);
      message.error({ content: error.response?.data?.message || 'Có lỗi xảy ra khi xử lý yêu cầu', key: 'action_error' });
      setReasonModal(prev => ({ ...prev, loading: false }));
    }
  };

  // 4. Open Process / Complete Note Modal
  const handleOpenNoteModal = (meeting, type = 'PROCESS') => {
    if (type === 'COMPLETE') {
      try {
        const recDateStr = typeof meeting.receptionDate === 'string' && meeting.receptionDate.includes('T')
          ? meeting.receptionDate.split('T')[0]
          : String(meeting.receptionDate || '');
        let startTimeStr = '00:00';
        const slotStr = meeting.timeSlot || meeting.slot || '';
        if (slotStr.includes('-')) {
          startTimeStr = slotStr.split('-')[0]?.trim() || '00:00';
        }
        if (recDateStr) {
          const slotStartDateTime = dayjs(`${recDateStr} ${startTimeStr}`, 'YYYY-MM-DD HH:mm');
          if (slotStartDateTime.isValid() && dayjs().isBefore(slotStartDateTime)) {
            message.warning({
              content: `Chưa đến thời gian của ca gặp lãnh đạo (${slotStr} ngày ${meeting.date || recDateStr}), không thể xác nhận tiếp xong!`,
              key: 'meeting_time_warning'
            });
            return;
          }
        }
      } catch (e) {
        console.warn('Error checking meeting time before opening complete modal:', e);
      }
    }

    setNoteModal({
      open: true,
      type,
      target: meeting,
      note: '',
      loading: false
    });
  };

  // 5. Confirm Process or Complete
  const handleConfirmNote = async () => {
    if (!noteModal.target || noteModal.loading) return;
    const targetId = noteModal.target.rawId || noteModal.target.id || noteModal.target.receptionId;
    const endpoint = noteModal.type === 'COMPLETE' 
      ? `/api/leader-meeting-registrations/${targetId}/complete`
      : `/api/leader-meeting-registrations/${targetId}/process`;

    const trimmed = noteModal.note.trim();
    if (noteModal.type === 'COMPLETE') {
      if (!trimmed || trimmed.length < 5) {
        message.error({ content: 'Vui lòng nhập kết quả xử lý tối thiểu 5 ký tự!', key: 'form_validation' });
        return;
      }
      if (trimmed.length > 2000) {
        message.error({ content: 'Kết quả xử lý không được vượt quá 2000 ký tự!', key: 'form_validation' });
        return;
      }
    }

    try {
      setNoteModal(prev => ({ ...prev, loading: true }));
      await apiClient.patch(endpoint, {
        note: trimmed || undefined
      });
      message.success({ content: noteModal.type === 'COMPLETE' ? 'Đã xác nhận hoàn tất buổi gặp lãnh đạo!' : 'Đã bắt đầu buổi gặp lãnh đạo!', key: 'action_success' });
      setNoteModal({ open: false, type: 'PROCESS', target: null, note: '', loading: false });
      fetchTickets(currentPage);
      fetchStats();
      if (selectedMeeting?.rawId === targetId) {
        setSelectedMeeting(prev => ({ 
          ...prev, 
          status: noteModal.type === 'COMPLETE' ? 'COMPLETED' : 'IN_PROGRESS',
          approvalStatus: noteModal.type === 'COMPLETE' ? 'COMPLETED' : 'IN_PROGRESS',
          processingResult: noteModal.type === 'COMPLETE' ? noteModal.note.trim() : prev.processingResult
        }));
      }
    } catch (error) {
      console.error("Action error", error);
      message.error({ content: error.response?.data?.message || 'Có lỗi xảy ra khi xử lý yêu cầu', key: 'action_error' });
      setNoteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const displayItems = useMemo(() => {
    if (!Array.isArray(dbTickets)) return [];
    const list = dbTickets
      .filter(isMyRegistration)
      .map(item => {
        const applicantObj = item.applicant || item.citizen || item.citizenInfo || {};
        const phoneVal = applicantObj.phoneNumber || applicantObj.phone || item.phone || item.phoneNumber || item.citizenPhone || '';
        const cccdVal = applicantObj.citizenId || applicantObj.identityCardNumber || applicantObj.cccd || item.cccd || item.citizenId || item.identityCardNumber || '';
        const addressVal = applicantObj.address || item.address || item.citizenAddress || '';
        const nameVal = applicantObj.fullName || applicantObj.name || item.citizenName || item.applicantName || item.fullName || 'Công dân';
        const topicVal = item.topic || item.reason || item.workingContent || item.content || item.title || '';
        const descVal = item.workingContent || item.reason || item.description || item.content || topicVal;
        const leaderVal = item.leader?.fullName || item.leaderName || item.schedule?.officerName || item.approver?.name || 'Lãnh đạo UBND';
        const rawStatus = (item.approvalStatus || item.status || 'PENDING').toUpperCase();
        const resultVal = item.processingResult || item.completionNote || item.workflow?.completionNote || item.ghi_chu_hoan_thanh || '';

        // Kiểm tra xem đơn PENDING đã trôi qua thời gian hẹn chưa (hoặc trạng thái là OVERDUE)
        let isOverdue = item.isOverdue;
        if (rawStatus === 'OVERDUE') {
          isOverdue = true;
        } else if (typeof isOverdue !== 'boolean' && item.receptionDate && rawStatus === 'PENDING') {
          try {
            const recDateStr = typeof item.receptionDate === 'string' && item.receptionDate.includes('T')
              ? item.receptionDate.split('T')[0]
              : String(item.receptionDate || '');
            
            let endTimeStr = '23:59';
            const slotStr = item.timeSlot || item.slot || '';
            if (slotStr.includes('-')) {
              const parts = slotStr.split('-');
              endTimeStr = parts[1]?.trim() || parts[0]?.trim() || '23:59';
            }
            
            if (recDateStr) {
              const slotEndDateTime = dayjs(`${recDateStr}T${endTimeStr}:00`);
              if (slotEndDateTime.isValid() && dayjs().isAfter(slotEndDateTime)) {
                isOverdue = true;
              }
            }
          } catch (e) {
            console.warn('Error checking overdue status:', e);
          }
        } else if (rawStatus !== 'PENDING') {
          isOverdue = false;
        }

        return {
          ...item,
          rawId: item.id,
          receptionId: item.id || item.receptionCode || item.registrationCode,
          ticketNo: item.registrationCode || item.receptionCode || item.ticketNo || item.id,
          citizenName: nameVal,
          fullName: nameVal,
          topic: topicVal,
          description: descVal,
          leaderName: leaderVal,
          date: item.receptionDate ? new Date(item.receptionDate).toLocaleDateString('vi-VN') : (item.date || 'Hôm nay'),
          slot: item.timeSlot || item.slot || 'Theo ca trực',
          office: item.location || item.schedule?.location || item.office || 'Phòng tiếp công dân',
          phone: phoneVal || '---',
          cccd: cccdVal || '---',
          address: addressVal || '---',
          currentStatusKey: rawStatus,
          processingResult: resultVal,
          isOverdue: Boolean(isOverdue)
        };
      });

    if (statusFilter === 'OVERDUE') {
      return list.filter(item => item.isOverdue);
    }

    return list;
  }, [dbTickets, statusFilter, isMyRegistration]);

  const getActionMenuItems = (item) => {
    const status = item.currentStatusKey;
    const isFinished = ['COMPLETED', 'REJECTED', 'CANCELED'].includes(status);
    const items = [
      {
        key: 'detail',
        label: (
          <span className="flex items-center gap-2 text-xs py-1 text-gray-700 font-medium">
            <Eye className="w-4 h-4 text-blue-600" />
            <span>Xem chi tiết đơn</span>
          </span>
        ),
        onClick: () => handleOpenDetail(item),
      },
    ];

    // Nếu là đơn bị QUÁ HẠN -> Chỉ cho phép Xem chi tiết đơn
    if (status === 'OVERDUE' || (status === 'PENDING' && item.isOverdue)) {
      return items;
    }

    // Trạng thái PENDING: Có thể Phê duyệt hoặc Từ chối
    if (status === 'PENDING') {
      items.push(
        {
          key: 'approve',
          label: (
            <span className="flex items-center gap-2 text-xs py-1 text-emerald-600 font-medium">
              <Check className="w-4 h-4" />
              <span>Phê duyệt lịch gặp</span>
            </span>
          ),
          onClick: () => handleApprove(item),
        },
        {
          key: 'reject',
          label: (
            <span className="flex items-center gap-2 text-xs py-1 text-red-600 font-medium">
              <X className="w-4 h-4" />
              <span>Từ chối đơn đăng ký</span>
            </span>
          ),
          onClick: () => handleOpenReasonModal(item, 'REJECT'),
        }
      );
    }

    // Trạng thái APPROVED: Chỉ có thể Hủy lịch hẹn (trước giờ tiếp)
    if (status === 'APPROVED') {
      items.push({
        key: 'cancel',
        label: (
          <span className="flex items-center gap-2 text-xs py-1 text-rose-600 font-medium">
            <Ban className="w-4 h-4" />
            <span>Hủy lịch hẹn</span>
          </span>
        ),
        onClick: () => handleOpenReasonModal(item, 'CANCEL'),
      });
    }

    // Trạng thái IN_PROGRESS: Có thể Hoàn thành (Xác nhận tiếp xong)
    if (status === 'IN_PROGRESS') {
      items.push({
        key: 'complete',
        label: (
          <span className="flex items-center gap-2 text-xs py-1 text-emerald-600 font-medium">
            <CheckCircle2 className="w-4 h-4" />
            <span>Xác nhận tiếp xong</span>
          </span>
        ),
        onClick: () => handleOpenNoteModal(item, 'COMPLETE'),
      });
    }

    return items;
  };

  const renderStatusBadge = (status, isOverdue = false) => {
    if (status === 'OVERDUE' || (status === 'PENDING' && isOverdue)) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200" title="Đơn đã quá hạn xử lý">
          <AlertCircle className="w-3 h-3 text-rose-600" /> Quá hạn
        </span>
      );
    }
    switch (status) {
      case 'OVERDUE':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200" title="Đơn đã quá hạn xử lý">
            <AlertCircle className="w-3 h-3 text-rose-600" /> Quá hạn
          </span>
        );
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-3 h-3" /> Chờ phê duyệt
          </span>
        );
      case 'APPROVED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            <CheckCircle2 className="w-3 h-3" /> Đã duyệt lịch
          </span>
        );
      case 'IN_PROGRESS':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
            <PlayCircle className="w-3 h-3 text-indigo-600" /> Đang xử lý
          </span>
        );
      case 'COMPLETED':
      case 'RATED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3" /> Đã hoàn thành
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
            <XCircle className="w-3 h-3" /> Đã từ chối
          </span>
        );
      case 'CANCELED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
            <Ban className="w-3 h-3" /> Đã hủy
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Quản lý lịch gặp lãnh đạo
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Kiểm tra đơn đăng ký, phê duyệt lịch gặp và xác nhận hoàn tất buổi tiếp công dân.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              fetchTickets(currentPage);
              fetchStats();
            }}
            className="h-9 px-3.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-lg text-xs md:text-sm font-medium shadow-2xs flex items-center gap-1.5 transition-all cursor-pointer"
          >
            <RotateCcw className={`w-4 h-4 text-gray-500 ${loading || statsLoading ? 'animate-spin' : ''}`} />
            <span>Làm mới</span>
          </button>
        </div>
      </div>

      {/* KHỐI 6 THẺ KPI STAT CARDS */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-3.5">
        {/* Card 1: Tổng số đơn */}
        <div
          onClick={() => setStatusFilter("")}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${
            !statusFilter
              ? "border-blue-500 ring-1 ring-blue-500/30 shadow-xs bg-blue-50/20"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Tổng số đơn</span>
              <span className="text-2xl font-bold text-blue-600 tracking-tight">
                {statsLoading ? '...' : stats.total}
              </span>
            </div>
            <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 2: Chờ phê duyệt */}
        <div
          onClick={() => setStatusFilter(prev => prev === "PENDING" ? "" : "PENDING")}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${
            statusFilter === "PENDING"
              ? "border-amber-500 ring-1 ring-amber-500/30 shadow-xs bg-amber-50/20"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Chờ phê duyệt</span>
              <span className="text-2xl font-bold text-amber-600 tracking-tight">
                {statsLoading ? '...' : stats.pending}
              </span>
            </div>
            <div className="w-9 h-9 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 3: Quá hạn */}
        <div
          onClick={() => setStatusFilter(prev => prev === "OVERDUE" ? "" : "OVERDUE")}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${
            statusFilter === "OVERDUE"
              ? "border-rose-500 ring-1 ring-rose-500/30 shadow-xs bg-rose-50/20"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Quá hạn</span>
              <span className="text-2xl font-bold text-rose-600 tracking-tight">
                {statsLoading ? '...' : stats.overdue}
              </span>
            </div>
            <div className="w-9 h-9 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center">
              <AlertCircle className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 4: Đã duyệt lịch */}
        <div
          onClick={() => setStatusFilter(prev => prev === "APPROVED" ? "" : "APPROVED")}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${
            statusFilter === "APPROVED"
              ? "border-emerald-500 ring-1 ring-emerald-500/30 shadow-xs bg-emerald-50/20"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Đã duyệt lịch</span>
              <span className="text-2xl font-bold text-emerald-600 tracking-tight">
                {statsLoading ? '...' : stats.approved}
              </span>
            </div>
            <div className="w-9 h-9 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 5: Đang xử lý */}
        <div
          onClick={() => setStatusFilter(prev => prev === "IN_PROGRESS" ? "" : "IN_PROGRESS")}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${
            statusFilter === "IN_PROGRESS"
              ? "border-indigo-500 ring-1 ring-indigo-500/30 shadow-xs bg-indigo-50/20"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Đang xử lý</span>
              <span className="text-2xl font-bold text-indigo-600 tracking-tight">
                {statsLoading ? '...' : stats.inProgress}
              </span>
            </div>
            <div className="w-9 h-9 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <PlayCircle className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 6: Đã hoàn thành */}
        <div
          onClick={() => setStatusFilter(prev => prev === "COMPLETED" ? "" : "COMPLETED")}
          className={`bg-white rounded-lg p-3.5 md:p-4 border shadow-xs transition-all cursor-pointer ${
            statusFilter === "COMPLETED"
              ? "border-teal-500 ring-1 ring-teal-500/30 shadow-xs bg-teal-50/20"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 block mb-1">Đã hoàn thành</span>
              <span className="text-2xl font-bold text-teal-600 tracking-tight">
                {statsLoading ? '...' : stats.completed}
              </span>
            </div>
            <div className="w-9 h-9 rounded-md bg-teal-50 text-teal-600 flex items-center justify-center">
              <UserCheck className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>


      {/* FILTER & SEARCH */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-2xs">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="w-full md:w-80 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Tìm mã đơn, họ tên công dân, lãnh đạo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs rounded-lg border border-gray-200 bg-gray-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="w-full md:w-auto flex flex-wrap items-center gap-2">
            {/* Filter Date */}
            <div className="w-36">
              <VietnameseDatePicker
                value={dateFilter}
                onChange={(isoVal) => setDateFilter(isoVal)}
                placeholder="dd/MM/yyyy"
              />
            </div>

            {/* Filter Status */}
            <div className="w-48">
              <CustomSelect
                value={statusFilter}
                onChange={(val) => setStatusFilter(typeof val === 'object' ? val?.target?.value : val)}
                placeholder="Tất cả trạng thái"
                options={[
                  { value: "", label: "Tất cả trạng thái" },
                  { value: "PENDING", label: "Chờ phê duyệt" },
                  { value: "OVERDUE", label: "Quá hạn" },
                  { value: "APPROVED", label: "Đã duyệt lịch" },
                  { value: "IN_PROGRESS", label: "Đang xử lý" },
                  { value: "COMPLETED", label: "Đã hoàn thành" },
                  { value: "REJECTED", label: "Đã từ chối" },
                  { value: "CANCELED", label: "Đã hủy" }
                ]}
              />
            </div>

            {/* Reset Filter Button */}
            {(search || statusFilter || dateFilter) && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setStatusFilter('');
                  setDateFilter('');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-xs flex items-center gap-1"
                title="Xóa bộ lọc"
              >
                <RotateCcw size={14} />
                <span className="hidden sm:inline">Đặt lại</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* TABLE SECTION */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xs overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs font-bold text-gray-700">
            Danh sách đơn đăng ký gặp lãnh đạo ({pagination.totalItems || displayItems.length})
          </span>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                <th className="pl-6 pr-3 py-3.5 whitespace-nowrap" style={{ width: '12%' }}>
                  Mã đơn
                </th>
                <th className="px-3 py-3.5" style={{ width: '16%' }}>
                  Người đăng ký
                </th>
                <th className="px-3 py-3.5" style={{ width: '24%' }}>
                  Nội dung & Lý do gặp
                </th>
                <th className="px-3 py-3.5 whitespace-nowrap" style={{ width: '16%' }}>
                  Lịch hẹn / Lãnh đạo
                </th>
                <th className="px-3 py-3.5" style={{ width: '15%' }}>
                  Kết quả xử lý
                </th>
                <th className="px-2 py-3.5 text-center whitespace-nowrap" style={{ width: '10%' }}>
                  Trạng thái
                </th>
                <th className="px-2 py-3.5 text-center whitespace-nowrap" style={{ width: '7%' }}>
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                    Đang tải danh sách đơn gặp lãnh đạo...
                  </td>
                </tr>
              ) : displayItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    Không có đơn đăng ký nào phù hợp.
                  </td>
                </tr>
              ) : (
                displayItems.map((item) => (
                  <tr key={item.receptionId} className="hover:bg-gray-50/80 transition-colors">
                    {/* Mã đơn */}
                    <td className="pl-6 pr-4 py-3.5 font-bold text-blue-600 whitespace-nowrap overflow-hidden">
                      <button
                        type="button"
                        onClick={() => handleOpenDetail(item)}
                        className="hover:underline hover:text-blue-800 text-left font-bold text-blue-600 cursor-pointer transition-colors truncate block max-w-full"
                        title={item.ticketNo}
                      >
                        {item.ticketNo}
                      </button>
                    </td>

                    {/* Người đăng ký */}
                    <td className="px-4 py-3.5 overflow-hidden">
                      <div className="font-semibold text-gray-900 text-[13px] truncate" title={item.citizenName}>
                        {item.citizenName}
                      </div>
                      <div className="text-gray-500 text-xs mt-0.5 flex items-center gap-1 truncate" title={item.phone}>
                        <Phone className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="truncate">{item.phone}</span>
                      </div>
                    </td>

                    {/* Nội dung */}
                    <td className="px-4 py-3.5 overflow-hidden">
                      <p className="text-gray-800 line-clamp-2 break-words leading-relaxed" title={item.topic || '---'}>
                        {item.topic || <span className="text-gray-400 italic">---</span>}
                      </p>
                    </td>

                    {/* Lịch hẹn & Lãnh đạo */}
                    <td className="px-4 py-3.5 overflow-hidden">
                      <div className="font-medium text-gray-900 text-xs truncate" title={`${item.slot} (${item.date})`}>
                        {item.slot} ({item.date})
                      </div>
                      <div className="text-blue-700 font-semibold text-xs mt-0.5 truncate" title={item.leaderName}>
                        {item.leaderName}
                      </div>
                    </td>

                    {/* Kết quả xử lý */}
                    <td className="px-4 py-3.5 overflow-hidden">
                      {item.processingResult ? (
                        <p className="text-gray-800 text-xs font-medium line-clamp-2 break-words leading-relaxed" title={item.processingResult}>
                          {item.processingResult}
                        </p>
                      ) : (
                        <span className="text-gray-400 text-xs italic">---</span>
                      )}
                    </td>

                    {/* Trạng thái */}
                    <td className="px-4 py-3.5 text-center whitespace-nowrap">
                      {renderStatusBadge(item.currentStatusKey, item.isOverdue)}
                    </td>

                    {/* Thao tác */}
                    <td className="px-4 py-3.5 text-center whitespace-nowrap">
                      <Dropdown
                        menu={{ items: getActionMenuItems(item) }}
                        trigger={['click']}
                        placement="bottomRight"
                      >
                        <button
                          type="button"
                          className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-blue-600 transition-colors inline-flex items-center justify-center cursor-pointer shadow-2xs"
                          title="Hành động"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {pagination.totalPages > 1 && (
          <div className="p-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50 text-xs text-gray-500">
            <span>
              Trang {currentPage} / {pagination.totalPages} ({pagination.totalItems} kết quả)
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => {
                  const p = Math.max(1, currentPage - 1);
                  setCurrentPage(p);
                  fetchTickets(p);
                }}
                className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                disabled={currentPage >= pagination.totalPages}
                onClick={() => {
                  const p = Math.min(pagination.totalPages, currentPage + 1);
                  setCurrentPage(p);
                  fetchTickets(p);
                }}
                className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL CHI TIẾT ĐƠN */}
      <Modal
        title={<div className="font-bold text-gray-900 text-base">Chi tiết đơn đăng ký gặp lãnh đạo</div>}
        open={Boolean(selectedMeeting)}
        onCancel={handleCloseDetail}
        footer={[
          <button
            key="close"
            onClick={handleCloseDetail}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Đóng
          </button>
        ]}
        width={720}
      >
        {detailLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
            <p className="text-xs text-gray-500 mt-2">Đang tải thông tin chi tiết...</p>
          </div>
        ) : selectedMeeting && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Mã đơn</span>
                <span className="font-bold text-blue-600 text-sm">{selectedMeeting.ticketNo || selectedMeeting.receptionId}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Trạng thái</span>
                <div className="mt-1">{renderStatusBadge(selectedMeeting.status || selectedMeeting.approvalStatus, selectedMeeting.isOverdue)}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Họ tên công dân</span>
                <span className="font-bold text-gray-900 text-sm">{selectedMeeting.citizenName}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Số điện thoại</span>
                <span className="font-medium text-gray-800 text-sm">{selectedMeeting.phone}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">CCCD / Định danh</span>
                <span className="font-medium text-gray-800 text-sm">{selectedMeeting.cccd}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block">Lãnh đạo tiếp</span>
                <span className="font-bold text-blue-700 text-sm">{selectedMeeting.leaderName}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg sm:col-span-2">
                <span className="text-xs text-gray-500 block">Thời gian & Địa điểm lịch hẹn</span>
                <span className="font-medium text-gray-800 text-sm">
                  {selectedMeeting.slot} ngày {selectedMeeting.date} ({selectedMeeting.office})
                </span>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-xs text-gray-500 block mb-1 font-semibold">Lý do & Nội dung gặp lãnh đạo</span>
              <p className="text-sm text-gray-800 font-medium whitespace-pre-wrap">
                {selectedMeeting.topic}
              </p>
              {selectedMeeting.description && selectedMeeting.description !== selectedMeeting.topic && (
                <p className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200 whitespace-pre-wrap">
                  {selectedMeeting.description}
                </p>
              )}
            </div>

            {/* KẾT QUẢ XỬ LÝ */}
            {(selectedMeeting.processingResult || selectedMeeting.workflow?.completionNote || selectedMeeting.completionNote) && (
              <div className="bg-emerald-50/80 p-3.5 rounded-lg border border-emerald-200">
                <span className="text-xs text-emerald-800 font-bold block mb-1 flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-emerald-600" />
                  Kết quả xử lý
                </span>
                <p className="text-xs text-emerald-950 font-medium whitespace-pre-wrap">
                  {selectedMeeting.processingResult || selectedMeeting.workflow?.completionNote || selectedMeeting.completionNote}
                </p>
              </div>
            )}

            {selectedMeeting.address && selectedMeeting.address !== '---' && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-xs text-gray-500 block mb-1 font-semibold">Địa chỉ công dân</span>
                <p className="text-xs text-gray-700">{selectedMeeting.address}</p>
              </div>
            )}

            {/* KHU VỰC ẢNH CĂN CƯỚC CÔNG DÂN (CCCD) */}
            {(frontCccd || backCccd || cccdLoading) && (
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-gray-800 uppercase tracking-wide flex items-center gap-1.5">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    Ảnh Căn cước công dân (CCCD)
                  </span>
                  <span className="text-[11px] text-gray-500 font-medium">Nhấp vào ảnh để phóng to</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* MẶT TRƯỚC */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200 space-y-2">
                    <div className="text-xs font-semibold text-gray-700 flex items-center justify-between">
                      <span>Mặt trước</span>
                      {frontCccd && <span className="text-[10px] text-gray-400 font-normal">{formatFileSize(frontCccd.size)}</span>}
                    </div>
                    {cccdLoading ? (
                      <div className="h-36 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      </div>
                    ) : frontCccd && attachmentBlobs[frontCccd.id] ? (
                      <div className="h-36 overflow-hidden rounded-lg border border-gray-100 flex items-center justify-center bg-gray-900">
                        <Image
                          src={attachmentBlobs[frontCccd.id]}
                          alt="CCCD Mặt trước"
                          className="object-contain max-h-36"
                          preview={{ mask: <div className="text-xs font-medium flex items-center gap-1"><Eye size={14} /> Phóng to</div> }}
                        />
                      </div>
                    ) : (
                      <div className="h-36 bg-gray-50 border border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center text-gray-400 text-xs">
                        <CreditCard className="w-6 h-6 mb-1 opacity-50" />
                        <span>Chưa có ảnh mặt trước</span>
                      </div>
                    )}
                  </div>

                  {/* MẶT SAU */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200 space-y-2">
                    <div className="text-xs font-semibold text-gray-700 flex items-center justify-between">
                      <span>Mặt sau</span>
                      {backCccd && <span className="text-[10px] text-gray-400 font-normal">{formatFileSize(backCccd.size)}</span>}
                    </div>
                    {cccdLoading ? (
                      <div className="h-36 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      </div>
                    ) : backCccd && attachmentBlobs[backCccd.id] ? (
                      <div className="h-36 overflow-hidden rounded-lg border border-gray-100 flex items-center justify-center bg-gray-900">
                        <Image
                          src={attachmentBlobs[backCccd.id]}
                          alt="CCCD Mặt sau"
                          className="object-contain max-h-36"
                          preview={{ mask: <div className="text-xs font-medium flex items-center gap-1"><Eye size={14} /> Phóng to</div> }}
                        />
                      </div>
                    ) : (
                      <div className="h-36 bg-gray-50 border border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center text-gray-400 text-xs">
                        <CreditCard className="w-6 h-6 mb-1 opacity-50" />
                        <span>Chưa có ảnh mặt sau</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* KHU VỰC HỒ SƠ & TÀI LIỆU ĐÍNH KÈM */}
            {supportingDocs.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2.5">
                <span className="text-xs font-bold text-gray-800 uppercase tracking-wide flex items-center gap-1.5">
                  <Paperclip className="w-4 h-4 text-blue-600" />
                  Hồ sơ & Tài liệu đính kèm ({supportingDocs.length})
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {supportingDocs.map((att, idx) => (
                    <div
                      key={att.id || idx}
                      className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-2xs transition-all"
                    >
                      <div className="flex items-center gap-2.5 overflow-hidden">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                          <FileText size={16} />
                        </div>
                        <div className="overflow-hidden">
                          <div className="text-xs font-bold text-gray-800 truncate" title={att.originalName}>
                            {att.originalName || `Tài liệu ${idx + 1}`}
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {formatFileSize(att.size)}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={downloadingAttId === att.id}
                        onClick={() => handleDownloadAttachment(selectedMeeting.rawId || selectedMeeting.id, att)}
                        className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer shrink-0 ml-2"
                      >
                        {downloadingAttId === att.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                        <span>Tải về</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons in Modal */}
            {!(selectedMeeting.status === 'OVERDUE' || (selectedMeeting.status === 'PENDING' && selectedMeeting.isOverdue)) && !['COMPLETED', 'REJECTED', 'CANCELED'].includes(selectedMeeting.status) && (
              <div className="pt-2 flex flex-wrap items-center justify-end gap-2 border-t border-gray-100">
                {selectedMeeting.status === 'PENDING' && (
                  <>
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => handleApprove(selectedMeeting)}
                      className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <Check className="w-3.5 h-3.5" /> Phê duyệt lịch gặp
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpenReasonModal(selectedMeeting, 'REJECT')}
                      className="px-3.5 py-1.5 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" /> Từ chối đơn
                    </button>
                  </>
                )}

                {selectedMeeting.status === 'APPROVED' && (
                  <button
                    type="button"
                    onClick={() => handleOpenReasonModal(selectedMeeting, 'CANCEL')}
                    className="px-3.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <Ban className="w-3.5 h-3.5" /> Hủy lịch hẹn
                  </button>
                )}

                {selectedMeeting.status === 'IN_PROGRESS' && (
                  <button
                    type="button"
                    onClick={() => handleOpenNoteModal(selectedMeeting, 'COMPLETE')}
                    className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Xác nhận tiếp xong
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* MODAL TỪ CHỐI / HỦY */}
      <Modal
        title={
          <div className="font-bold text-red-600 text-base">
            {reasonModal.type === 'CANCEL' ? 'Hủy lịch hẹn gặp lãnh đạo' : 'Từ chối đơn đăng ký gặp lãnh đạo'}
          </div>
        }
        open={reasonModal.open}
        onOk={handleConfirmReason}
        confirmLoading={reasonModal.loading}
        onCancel={() => setReasonModal({ open: false, type: 'REJECT', target: null, reason: '', loading: false })}
        okText={reasonModal.type === 'CANCEL' ? 'Xác nhận hủy lịch' : 'Xác nhận từ chối'}
        cancelText="Đóng"
        okButtonProps={{ danger: true }}
      >
        <p className="text-sm text-gray-600 mb-3">
          Vui lòng nhập lý do {reasonModal.type === 'CANCEL' ? 'hủy lịch hẹn' : 'từ chối đơn'} của công dân <strong>{reasonModal.target?.citizenName}</strong>:
        </p>
        <textarea
          rows={3}
          maxLength={2000}
          value={reasonModal.reason}
          onChange={(e) => setReasonModal({ ...reasonModal, reason: e.target.value })}
          placeholder="Nhập lý do chi tiết (tối thiểu 5 ký tự)..."
          className="w-full p-2.5 text-xs rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
        />
        <div className="flex items-center justify-between mt-1 text-[11px]">
          <span className={reasonModal.reason.trim().length < 5 ? "text-amber-600 font-medium" : "text-emerald-600 font-medium"}>
            {reasonModal.reason.trim().length < 5
              ? `* Tối thiểu 5 ký tự (còn thiếu ${Math.max(0, 5 - reasonModal.reason.trim().length)} ký tự)`
              : '✓ Độ dài hợp lệ'}
          </span>
          <span className="text-gray-400 font-medium">{reasonModal.reason.length} / 2000</span>
        </div>
      </Modal>

      {/* MODAL GHI CHÚ TIẾN ĐỘ (PROCESS / COMPLETE) */}
      <Modal
        title={
          <div className="font-bold text-blue-700 text-base">
            {noteModal.type === 'COMPLETE' ? 'Xác nhận hoàn tất & nhập kết quả xử lý' : 'Bắt đầu buổi gặp lãnh đạo'}
          </div>
        }
        open={noteModal.open}
        onOk={handleConfirmNote}
        confirmLoading={noteModal.loading}
        onCancel={() => setNoteModal({ open: false, type: 'PROCESS', target: null, note: '', loading: false })}
        okText={noteModal.type === 'COMPLETE' ? 'Xác nhận hoàn tất' : 'Xác nhận'}
        cancelText="Hủy"
      >
        <div className="mb-2">
          {noteModal.type === 'COMPLETE' ? (
            <p className="text-sm text-gray-800">
              Vui lòng nhập <strong>kết quả xử lý</strong> cho công dân <strong>{noteModal.target?.citizenName}</strong> <span className="text-red-500 font-bold">*</span>:
            </p>
          ) : (
            <p className="text-sm text-gray-600">Xác nhận bắt đầu tiếp công dân và ghi chú thêm (Không bắt buộc):</p>
          )}
        </div>
        <textarea
          rows={4}
          maxLength={2000}
          value={noteModal.note}
          onChange={(e) => setNoteModal({ ...noteModal, note: e.target.value })}
          placeholder={noteModal.type === 'COMPLETE' ? 'Nhập chi tiết nội dung kết quả xử lý (tối thiểu 5 ký tự)...' : 'Nhập ghi chú thêm nếu có...'}
          className="w-full p-2.5 text-xs rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex items-center justify-between mt-1 text-[11px]">
          {noteModal.type === 'COMPLETE' ? (
            <span className={noteModal.note.trim().length < 5 ? "text-amber-600 font-medium" : "text-emerald-600 font-medium"}>
              {noteModal.note.trim().length < 5
                ? `* Tối thiểu 5 ký tự (còn thiếu ${Math.max(0, 5 - noteModal.note.trim().length)} ký tự)`
                : '✓ Độ dài hợp lệ'}
            </span>
          ) : (
            <span className="text-gray-400">Không bắt buộc</span>
          )}
          <span className="text-gray-400 font-medium">{noteModal.note.length} / 2000</span>
        </div>
      </Modal>
    </div>
  );
}
